<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <form @submit.prevent="guardarOrden">
          <!-- Header -->
          <header class="entrada-header">
            <div>
              <p class="almacen-title">Orden de entrada a almacén</p>
              <p class="almacen-subtitle">
                Registra productos nuevos o existentes, sus complementos y series en un solo flujo.
              </p>
            </div>

            <div class="entrada-header__actions">
              <button
                type="submit"
                class="ios-primary-button"
                :disabled="guardando || !items.length"
              >
                {{ guardando ? 'Guardando...' : 'Guardar orden' }}
              </button>
            </div>
          </header>

          <!-- Datos de encabezado -->
          <section class="entrada-meta">
            <div class="entrada-meta__row">
              <div class="entrada-field">
                <label class="entrada-label">Fecha</label>
                <input
                  type="date"
                  v-model="encabezado.fecha"
                  class="entrada-input"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Folio</label>
                <input
                  type="text"
                  v-model="encabezado.folio"
                  class="entrada-input"
                  placeholder="Ej. OC-0001"
                />
              </div>

              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Proveedor</label>
                <input
                  type="text"
                  v-model="encabezado.proveedor"
                  class="entrada-input"
                  placeholder="Nombre del proveedor"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field">
                <label class="entrada-label">Solicitó</label>
                <input
                  type="text"
                  v-model="encabezado.solicito"
                  class="entrada-input"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Entregó</label>
                <input
                  type="text"
                  v-model="encabezado.entrego"
                  class="entrada-input"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Recibió</label>
                <input
                  type="text"
                  v-model="encabezado.recibio"
                  class="entrada-input"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Dirección de recepción</label>
                <input
                  type="text"
                  v-model="encabezado.direccion_recepcion"
                  class="entrada-input"
                  placeholder="Bodega / sucursal donde se recibe"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Comentarios generales</label>
                <textarea
                  v-model="encabezado.comentarios"
                  class="entrada-input entrada-input--textarea"
                  rows="2"
                  placeholder="Notas generales de esta orden de entrada..."
                ></textarea>
              </div>
            </div>
          </section>

          <!-- Items / Productos -->
          <section class="entrada-items">
            <div
              v-for="(item, index) in items"
              :key="item.uid"
              class="entrada-item-card"
            >
              <div class="entrada-item-header">
                <span class="entrada-item-badge">Producto #{{ index + 1 }}</span>

                <button
                  v-if="items.length > 1"
                  type="button"
                  class="entrada-item-remove"
                  @click="eliminarItem(index)"
                >
                  Eliminar
                </button>
              </div>

              <!-- Tipo de producto -->
              <div class="entrada-item-row">
                <div class="entrada-toggle">
                  <button
                    type="button"
                    class="entrada-toggle-pill"
                    :class="{ 'entrada-toggle-pill--active': item.tipoProducto === 'existente' }"
                    @click="cambiarTipoProducto(item, 'existente')"
                  >
                    Producto existente
                  </button>
                  <button
                    type="button"
                    class="entrada-toggle-pill"
                    :class="{ 'entrada-toggle-pill--active': item.tipoProducto === 'nuevo' }"
                    @click="cambiarTipoProducto(item, 'nuevo')"
                  >
                    Producto nuevo
                  </button>
                </div>
              </div>

              <!-- Producto existente -->
              <div
                v-if="item.tipoProducto === 'existente'"
                class="entrada-item-row"
              >
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Producto principal</label>
                  <SelectorProducto
                    :productos="productosPrincipales"
                    v-model="item.id_producto"
                    @seleccionado="(prod) => alSeleccionarProducto(item, prod)"
                  />
                </div>
              </div>

              <!-- Producto nuevo -->
              <div
                v-else
                class="entrada-item-row entrada-item-row--two-cols"
              >
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Nombre del producto</label>
                  <input
                    type="text"
                    v-model="item.productoNuevo.nombre"
                    class="entrada-input"
                    placeholder="Ej. Laptop Gamer 15”"
                  />
                </div>

                <div class="entrada-field">
                  <label class="entrada-label">Categoría</label>
                  <select
                    v-model="item.productoNuevo.id_categoria"
                    class="entrada-input"
                  >
                    <option value="">Sin categoría</option>
                    <option
                      v-for="cat in categorias"
                      :key="cat.id_categoria"
                      :value="cat.id_categoria"
                    >
                      {{ cat.nombre }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-if="item.tipoProducto === 'nuevo'" class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Descripción</label>
                  <textarea
                    v-model="item.productoNuevo.descripcion"
                    class="entrada-input entrada-input--textarea"
                    rows="2"
                    placeholder="Descripción corta del producto..."
                  ></textarea>
                </div>

                <div class="entrada-field">
                  <label class="entrada-label">Imagen principal</label>
                  <div class="entrada-photo-box entrada-photo-box--compact">
                    <div v-if="item.productoNuevo.imagen_principal_preview" class="entrada-photo-preview">
                      <img :src="item.productoNuevo.imagen_principal_preview" alt="Producto nuevo" />
                    </div>
                    <div v-else class="entrada-photo-empty">Sin imagen</div>

                    <input
                      :ref="'productoImagen_' + item.uid"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      class="entrada-photo-input"
                      @change="onImagenProductoNuevo(item, $event)"
                    />

                    <div class="entrada-photo-actions">
                      <button
                        type="button"
                        class="entrada-chip-button"
                        @click="abrirInputProducto(item)"
                      >
                        {{ item.productoNuevo.imagen_principal_preview ? 'Cambiar' : 'Cargar' }}
                      </button>
                      <button
                        v-if="item.productoNuevo.imagen_principal_preview"
                        type="button"
                        class="entrada-chip-button"
                        @click="quitarImagenProducto(item)"
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="item.tipoProducto === 'nuevo'" class="entrada-item-row entrada-item-row--models">
                <div class="entrada-field entrada-field--grow">
                  <div class="entrada-inline-head">
                    <label class="entrada-label">Modelos del producto</label>
                    <button
                      type="button"
                      class="entrada-chip-button"
                      @click="agregarModeloAProducto(item.productoNuevo)"
                    >
                      + Modelo
                    </button>
                  </div>
                  <p class="entrada-hint entrada-hint--mb">
                    Opcional. Si este producto maneja variantes, regístralas aquí.
                  </p>

                  <div v-if="!item.productoNuevo.modelos.length" class="entrada-modelos-empty">
                    Sin modelos registrados.
                  </div>

                  <div v-else class="entrada-modelos-list">
                    <div
                      v-for="(modelo, modeloIndex) in item.productoNuevo.modelos"
                      :key="modelo.uid || modeloIndex"
                      class="entrada-modelo-item"
                    >
                      <input
                        v-model="modelo.nombre"
                        type="text"
                        class="entrada-input"
                        placeholder="Nombre del modelo"
                      />
                      <input
                        v-model="modelo.modelo"
                        type="text"
                        class="entrada-input"
                        placeholder="Clave / modelo"
                      />
                      <button
                        type="button"
                        class="entrada-chip-button"
                        @click="eliminarModeloDeProducto(item.productoNuevo, modeloIndex)"
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Complementos -->
              <div
                v-if="item.tipoProducto === 'nuevo' || item.id_producto"
                class="entrada-item-section"
              >
                <div class="entrada-item-section-header">
                  <p class="entrada-label">Complementos</p>

                  <div class="entrada-item-section-actions">
                    <select
                      v-model="item._nuevoComplementoExistenteId"
                      class="entrada-input entrada-input--compact"
                    >
                      <option value="">Complemento existente…</option>
                      <option
                        v-for="comp in productosComplemento"
                        :key="comp.id_producto"
                        :value="comp.id_producto"
                      >
                        {{ comp.nombre }}
                      </option>
                    </select>

                    <button
                      type="button"
                      class="entrada-chip-button"
                      @click="agregarComplementoExistente(item)"
                    >
                      + Añadir
                    </button>

                    <button
                      type="button"
                      class="entrada-chip-button"
                      @click="agregarComplementoNuevo(item)"
                    >
                      + Complemento nuevo
                    </button>
                  </div>
                </div>

                <div
                  v-if="!item.complementos.length"
                  class="entrada-hint"
                >
                  Puedes dejar este producto sin complementos o agregarlos si aplica.
                </div>

                <div
                  v-else
                  class="entrada-chips"
                >
                  <div
                    v-for="(comp, cIndex) in item.complementos"
                    :key="cIndex"
                    class="entrada-chip"
                  >
                    <span class="entrada-chip-main">
                      {{ etiquetaComplemento(comp) }}
                      <span
                        v-if="comp.tipo === 'nuevo'"
                        class="entrada-chip-tag"
                      >
                        nuevo
                      </span>
                    </span>

                    <button
                      type="button"
                      class="entrada-chip-remove"
                      @click="eliminarComplemento(item, cIndex)"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <!-- Campos extra para complementos nuevos -->
                <div
                  v-for="(comp, cIndex) in item.complementos"
                  :key="'form-'+cIndex"
                >
                  <div
                    v-if="comp.tipo === 'nuevo'"
                    class="entrada-item-row entrada-item-row--inner"
                  >
                    <div class="entrada-field entrada-field--grow">
                      <label class="entrada-label">
                        Nombre complemento #{{ cIndex + 1 }}
                      </label>
                      <input
                        type="text"
                        v-model="comp.productoNuevo.nombre"
                        class="entrada-input"
                        placeholder="Ej. Cargador 65W"
                      />
                    </div>

                    <div class="entrada-field">
                      <label class="entrada-label">Categoría</label>
                      <select
                        v-model="comp.productoNuevo.id_categoria"
                        class="entrada-input"
                      >
                        <option value="">Sin categoría</option>
                        <option
                          v-for="cat in categorias"
                          :key="cat.id_categoria"
                          :value="cat.id_categoria"
                        >
                          {{ cat.nombre }}
                        </option>
                      </select>
                    </div>

                    <div class="entrada-field">
                      <label class="entrada-label">Imagen</label>
                      <div class="entrada-photo-box entrada-photo-box--compact">
                        <div v-if="comp.productoNuevo.imagen_principal_preview" class="entrada-photo-preview">
                          <img :src="comp.productoNuevo.imagen_principal_preview" alt="Complemento nuevo" />
                        </div>
                        <div v-else class="entrada-photo-empty">Sin imagen</div>

                        <input
                          :ref="'complementoImagen_' + item.uid + '_' + cIndex"
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          class="entrada-photo-input"
                          @change="onImagenComplementoNuevo(comp, item, cIndex, $event)"
                        />

                        <div class="entrada-photo-actions">
                          <button
                            type="button"
                            class="entrada-chip-button"
                            @click="abrirInputComplemento(item, cIndex)"
                          >
                            {{ comp.productoNuevo.imagen_principal_preview ? 'Cambiar' : 'Cargar' }}
                          </button>
                          <button
                            v-if="comp.productoNuevo.imagen_principal_preview"
                            type="button"
                            class="entrada-chip-button"
                            @click="quitarImagenComplemento(comp)"
                          >
                            Quitar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>


                  <div
                    v-if="comp.tipo === 'nuevo'"
                    class="entrada-item-row entrada-item-row--inner"
                  >
                    <div class="entrada-field entrada-field--grow">
                      <div class="entrada-inline-head">
                        <label class="entrada-label">Modelos del complemento #{{ cIndex + 1 }}</label>
                        <button
                          type="button"
                          class="entrada-chip-button"
                          @click="agregarModeloAProducto(comp.productoNuevo)"
                        >
                          + Modelo
                        </button>
                      </div>
                      <div v-if="!comp.productoNuevo.modelos.length" class="entrada-modelos-empty">
                        Sin modelos registrados.
                      </div>
                      <div v-else class="entrada-modelos-list">
                        <div
                          v-for="(modelo, modeloIndex) in comp.productoNuevo.modelos"
                          :key="modelo.uid || modeloIndex"
                          class="entrada-modelo-item"
                        >
                          <input
                            v-model="modelo.nombre"
                            type="text"
                            class="entrada-input"
                            placeholder="Nombre del modelo"
                          />
                          <input
                            v-model="modelo.modelo"
                            type="text"
                            class="entrada-input"
                            placeholder="Clave / modelo"
                          />
                          <button
                            type="button"
                            class="entrada-chip-button"
                            @click="eliminarModeloDeProducto(comp.productoNuevo, modeloIndex)"
                          >
                            Quitar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Cantidad + unidades / series -->
              <div
                v-if="item.tipoProducto === 'nuevo' || item.id_producto"
                class="entrada-item-section"
              >
                <div class="entrada-item-section-header">
                  <div class="entrada-field entrada-field--inline">
                    <label class="entrada-label">Cantidad de unidades</label>
                    <div class="entrada-counter">
                      <button
                        type="button"
                        class="entrada-counter-btn"
                        @click="ajustarCantidad(item, -1)"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        v-model.number="item.cantidad"
                        @change="onCantidadChange(item)"
                        class="entrada-input entrada-input--counter"
                      />
                      <button
                        type="button"
                        class="entrada-counter-btn"
                        @click="ajustarCantidad(item, 1)"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!item.unidades.length"
                  class="entrada-hint"
                >
                  Define la cantidad para generar los bloques de series.
                </div>

                <div
                  v-else
                  class="entrada-bulk-shortcuts"
                >
                  <div class="entrada-item-section-header">
                    <p class="entrada-label">Configuración rápida para este producto</p>
                  </div>

                  <p class="entrada-hint entrada-hint--mb">
                    Usa estos atajos para copiar ubicación, categoría o modelo a todas las unidades de este producto. La evidencia inicial se captura de forma individual dentro de cada serie.
                  </p>

                  <div class="entrada-bulk-shortcuts__grid">
                    <div class="entrada-bulk-shortcuts__card entrada-bulk-shortcuts__card--wide">
                      <label class="entrada-label">Atajo de ubicación</label>
                      <p class="entrada-hint entrada-hint--mb">
                        Define una ubicación base y aplícala a todas las unidades sólo cuando te convenga.
                      </p>

                      <SelectorUbicacionAlmacen
                        :model-value="{ id_almacen: item.id_almacen, id_ubicacion_almacen: item.id_ubicacion_almacen }"
                        :almacenes="almacenesCatalogo"
                        @update:modelValue="(value) => onAtajoUbicacionItemChange(item, value)"
                        @created="(evento) => onUbicacionCreadaItem(item, evento)"
                      />

                      <div class="entrada-shortcut-actions">
                        <button
                          type="button"
                          class="ios-ghost-button"
                          :disabled="!item.id_almacen || !item.id_ubicacion_almacen"
                          @click="aplicarUbicacionAtajoAItem(item)"
                        >
                          Aplicar ubicación a todas
                        </button>
                      </div>
                    </div>

                    <div class="entrada-bulk-shortcuts__card">
                      <label class="entrada-label">Categoría de unidad</label>
                      <p class="entrada-hint entrada-hint--mb">
                        Elige si esta carga entra como nueva o usada y aplícala de una vez.
                      </p>

                      <select
                        v-model="item.atajoCategoriaUnidad"
                        class="entrada-input entrada-input--compact"
                        @change="item.atajoCategoriaAplicada = false"
                      >
                        <option value="nuevo">Nuevo</option>
                        <option value="usado">Usado</option>
                      </select>

                      <div class="entrada-shortcut-actions">
                        <button
                          type="button"
                          class="ios-ghost-button"
                          @click="aplicarCategoriaAtajoAItem(item)"
                        >
                          Aplicar categoría a todas
                        </button>
                      </div>
                    </div>

                    <div
                      v-if="modelosDisponiblesParaItem(item).length"
                      class="entrada-bulk-shortcuts__card"
                    >
                      <label class="entrada-label">Modelo del producto</label>
                      <p class="entrada-hint entrada-hint--mb">
                        Si todas las unidades comparten el mismo modelo, selecciónalo aquí y cópialo a toda la carga.
                      </p>

                      <select
                        v-model="item.atajoModeloProducto"
                        class="entrada-input entrada-input--compact"
                        @change="item.atajoModeloAplicado = false"
                      >
                        <option value="">Selecciona un modelo…</option>
                        <option
                          v-for="modelo in modelosDisponiblesParaItem(item)"
                          :key="'atajo-modelo-' + (modelo.id_modelo_producto || modelo.uid || (modelo.nombre + '-' + modelo.modelo))"
                          :value="String(modelo.id_modelo_producto || modelo.uid || (modelo.nombre + '-' + modelo.modelo))"
                        >
                          {{ modelo.nombre }} · {{ modelo.modelo }}
                        </option>
                      </select>

                      <div class="entrada-shortcut-actions">
                        <button
                          type="button"
                          class="ios-ghost-button"
                          :disabled="!item.atajoModeloProducto"
                          @click="aplicarModeloAtajoAItem(item)"
                        >
                          Aplicar modelo a todas
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="item.unidades.length"
                  class="entrada-units-list"
                >
                  <div
                    v-for="(unidad, uIndex) in item.unidades"
                    :key="uIndex"
                    class="entrada-unit-card"
                  >
                    <div class="entrada-unit-header">
                      <span class="entrada-unit-badge">
                        Unidad #{{ uIndex + 1 }}
                      </span>
                      <span
                        v-if="nombreProductoParaItem(item)"
                        class="entrada-unit-product"
                      >
                        {{ nombreProductoParaItem(item) }}
                      </span>
                    </div>

                    <div class="entrada-field">
                      <label class="entrada-label">Serie producto principal (opcional)</label>
                      <input
                        v-model="unidad.serie_principal"
                        class="entrada-input"
                        :placeholder="nombreProductoParaItem(item) + ' - ' + (uIndex + 1)"
                      />
                    </div>

                    <div class="entrada-item-row entrada-item-row--two-cols entrada-item-row--inner">
                      <div class="entrada-field entrada-field--grow">
                        <label class="entrada-label">Categoría de la unidad</label>
                        <select
                          v-model="unidad.categoria_unidad"
                          class="entrada-input"
                        >
                          <option value="nuevo">Nuevo</option>
                          <option value="usado">Usado</option>
                        </select>
                      </div>

                      <div
                        v-if="modelosDisponiblesParaItem(item).length"
                        class="entrada-field entrada-field--grow"
                      >
                        <label class="entrada-label">Modelo del producto</label>
                        <select
                          v-model="unidad.id_modelo_producto"
                          class="entrada-input"
                        >
                          <option value="">Selecciona un modelo…</option>
                          <option
                            v-for="modelo in modelosDisponiblesParaItem(item)"
                            :key="modelo.id_modelo_producto || modelo.uid || (modelo.nombre + '-' + modelo.modelo)"
                            :value="String(modelo.id_modelo_producto || modelo.uid || (modelo.nombre + '-' + modelo.modelo))"
                          >
                            {{ modelo.nombre }} · {{ modelo.modelo }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="entrada-item-row entrada-item-row--two-cols entrada-item-row--inner">
                      <div class="entrada-field entrada-field--grow">
                        <label class="entrada-label">Descripción del estado inicial</label>
                        <textarea
                          v-model="unidad.descripcion_estado"
                          class="entrada-input entrada-input--textarea"
                          rows="2"
                          :placeholder="'Ej. Estado individual de la unidad #' + (uIndex + 1)"
                        ></textarea>
                      </div>

                      <div class="entrada-field">
                        <label class="entrada-label">Foto del estado inicial</label>
                        <div class="entrada-photo-box">
                          <div v-if="unidad.imagen_estado_preview" class="entrada-photo-preview">
                            <img :src="unidad.imagen_estado_preview" :alt="'Estado inicial de la unidad ' + (uIndex + 1)" />
                          </div>
                          <div v-else class="entrada-photo-empty">Sin foto individual</div>

                          <input
                            :ref="'unidadEvidenciaImagen_' + item.uid + '_' + uIndex"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            class="entrada-photo-input"
                            @change="onImagenEvidenciaUnidad(item, unidad, uIndex, $event)"
                          />

                          <div class="entrada-photo-actions">
                            <button
                              type="button"
                              class="entrada-chip-button"
                              @click="abrirInputEvidenciaUnidad(item, uIndex)"
                            >
                              {{ unidad.imagen_estado_preview ? 'Cambiar' : 'Cargar' }}
                            </button>
                            <button
                              v-if="unidad.imagen_estado_preview"
                              type="button"
                              class="entrada-chip-button"
                              @click="quitarImagenEvidenciaUnidad(item, unidad, uIndex)"
                            >
                              Quitar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="entrada-field entrada-field--inner">
                      <label class="entrada-label">Ubicación individual de esta unidad</label>
                      <SelectorUbicacionAlmacen
                        :model-value="{ id_almacen: unidad.id_almacen, id_ubicacion_almacen: unidad.id_ubicacion_almacen }"
                        :almacenes="almacenesCatalogo"
                        @update:modelValue="(value) => onUbicacionUnidadChange(unidad, value)"
                        @created="(evento) => onUbicacionCreadaUnidad(item, unidad, evento)"
                      />
                    </div>

                    <div
                      v-for="(uc, ucIndex) in unidad.complementos"
                      :key="ucIndex"
                      class="entrada-field entrada-field--inner"
                    >
                      <label class="entrada-label">
                        Serie complemento
                        <span class="entrada-label-pill">
                          {{ nombreComplementoPorIndice(item, uc.indice_complemento) }}
                        </span>
                      </label>
                      <input
                        v-model="uc.serie"
                        class="entrada-input"
                        :placeholder="nombreComplementoPorIndice(item, uc.indice_complemento) + ' - ' + (uIndex + 1)"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Comentarios del item -->
              <div
                v-if="item.tipoProducto === 'nuevo' || item.id_producto"
                class="entrada-item-section"
              >
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Comentarios del producto</label>
                  <textarea
                    v-model="item.comentarios"
                    class="entrada-input entrada-input--textarea"
                    rows="2"
                    placeholder="Notas específicas de este producto en la orden..."
                  ></textarea>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="ios-ghost-button entrada-add-item"
              @click="agregarItem"
            >
              + Agregar otro producto a la orden
            </button>
          </section>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SelectorProducto from '@/components/ui/SelectorProducto.vue';
import SelectorUbicacionAlmacen from '@/components/almacen/almacenes/SelectorUbicacionAlmacen.vue';

export default {
  name: 'CrearOrdenEntrada',
  components: { SelectorProducto, SelectorUbicacionAlmacen },
  data() {
    return {
      encabezado: {
        fecha: new Date().toISOString().slice(0, 10),
        folio: '',
        proveedor: '',
        solicito: '',
        entrego: '',
        recibio: '',
        direccion_recepcion: '',
        comentarios: '',
      },
      items: [],
      productosPrincipales: [],
      productosComplemento: [],
      categorias: [],
      almacenesCatalogo: [],
      cargandoInicial: false,
      guardando: false,
    };
  },
  mounted() {
    this.cargarCatalogos();
    this.agregarItem();
  },
  methods: {
    async archivoABase64(file) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    getFileInput(refName) {
      const ref = this.$refs[refName];
      if (Array.isArray(ref)) return ref[0] || null;
      return ref || null;
    },

    abrirInputProducto(item) {
      this.getFileInput('productoImagen_' + item.uid)?.click();
    },

    abrirInputComplemento(item, cIndex) {
      this.getFileInput('complementoImagen_' + item.uid + '_' + cIndex)?.click();
    },

    abrirInputEvidenciaUnidad(item, uIndex) {
      this.getFileInput('unidadEvidenciaImagen_' + item.uid + '_' + uIndex)?.click();
    },

    async onImagenProductoNuevo(item, event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const base64 = await this.archivoABase64(file);
      item.productoNuevo.imagen_principal_base64 = base64;
      item.productoNuevo.imagen_principal_nombre = file.name || 'producto';
      item.productoNuevo.imagen_principal_preview = base64;
    },

    quitarImagenProducto(item) {
      item.productoNuevo.imagen_principal_base64 = '';
      item.productoNuevo.imagen_principal_nombre = '';
      item.productoNuevo.imagen_principal_preview = '';
    },

    async onImagenComplementoNuevo(comp, item, cIndex, event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const base64 = await this.archivoABase64(file);
      comp.productoNuevo.imagen_principal_base64 = base64;
      comp.productoNuevo.imagen_principal_nombre = file.name || 'complemento';
      comp.productoNuevo.imagen_principal_preview = base64;
    },

    quitarImagenComplemento(comp) {
      comp.productoNuevo.imagen_principal_base64 = '';
      comp.productoNuevo.imagen_principal_nombre = '';
      comp.productoNuevo.imagen_principal_preview = '';
    },

    async onImagenEvidenciaUnidad(item, unidad, uIndex, event) {
      const file = event?.target?.files?.[0];
      if (!file || !unidad) return;
      const base64 = await this.archivoABase64(file);
      unidad.imagen_estado_base64 = base64;
      unidad.imagen_estado_nombre = file.name || ('evidencia-unidad-' + (uIndex + 1));
      unidad.imagen_estado_preview = base64;
    },

    quitarImagenEvidenciaUnidad(item, unidad, uIndex) {
      if (!unidad) return;
      unidad.imagen_estado_base64 = '';
      unidad.imagen_estado_nombre = '';
      unidad.imagen_estado_preview = '';
      const input = this.getFileInput('unidadEvidenciaImagen_' + item.uid + '_' + uIndex);
      if (input) input.value = '';
    },

    onAtajoUbicacionItemChange(item, value) {
      item.id_almacen = value?.id_almacen || '';
      item.id_ubicacion_almacen = value?.id_ubicacion_almacen || '';
      item.atajoUbicacionAplicado = false;
    },

    onUbicacionUnidadChange(unidad, value) {
      if (!unidad) return;
      unidad.id_almacen = value?.id_almacen || '';
      unidad.id_ubicacion_almacen = value?.id_ubicacion_almacen || '';
    },

    onUbicacionCreadaItem(item, evento) {
      this.registrarUbicacionCreada(evento, item, null);
    },

    onUbicacionCreadaUnidad(item, unidad, evento) {
      this.registrarUbicacionCreada(evento, item, unidad);
    },

    registrarUbicacionCreada(evento, item = null, unidad = null) {
      const nuevaUbicacion = this.normalizarUbicacionCreada(evento);
      if (!nuevaUbicacion) return;

      this.insertarUbicacionEnCatalogo(nuevaUbicacion);

      if (item && !unidad) {
        item.id_almacen = nuevaUbicacion.id_almacen;
        item.id_ubicacion_almacen = nuevaUbicacion.id_ubicacion_almacen;
        item.atajoUbicacionAplicado = false;
      }

      if (unidad) {
        unidad.id_almacen = nuevaUbicacion.id_almacen;
        unidad.id_ubicacion_almacen = nuevaUbicacion.id_ubicacion_almacen;
      }
    },

    normalizarUbicacionCreada(evento) {
      const payload = evento?.payload || {};
      const idAlmacen = String(payload.id_almacen || evento?.id_almacen || '');
      const idUbicacion = String(evento?.id_ubicacion_almacen || payload.id_ubicacion_almacen || '');

      if (!idAlmacen || !idUbicacion) {
        return null;
      }

      const nivel1 = payload.nivel_1 || '';
      const nivel2 = payload.nivel_2 || '';
      const nivel3 = payload.nivel_3 || '';
      const referencia = payload.referencia || '';

      return {
        id_ubicacion_almacen: idUbicacion,
        id_almacen: idAlmacen,
        nivel_1: nivel1,
        nivel_2: nivel2,
        nivel_3: nivel3,
        referencia,
        ubicacion_label: this.construirUbicacionLabel(nivel1, nivel2, nivel3),
      };
    },

    construirUbicacionLabel(nivel1, nivel2, nivel3) {
      return [nivel1, nivel2, nivel3].filter(Boolean).join(' / ');
    },

    insertarUbicacionEnCatalogo(ubicacion) {
      if (!ubicacion?.id_almacen || !ubicacion?.id_ubicacion_almacen) return;

      const catalogo = Array.isArray(this.almacenesCatalogo)
        ? JSON.parse(JSON.stringify(this.almacenesCatalogo))
        : [];

      const idxAlmacen = catalogo.findIndex(
        (almacen) => String(almacen.id_almacen) === String(ubicacion.id_almacen),
      );
      if (idxAlmacen === -1) return;

      const almacen = catalogo[idxAlmacen];
      if (!Array.isArray(almacen.ubicaciones)) {
        almacen.ubicaciones = [];
      }

      const existente = almacen.ubicaciones.find(
        (itemExistente) => String(itemExistente.id_ubicacion_almacen) === String(ubicacion.id_ubicacion_almacen),
      );

      if (existente) {
        Object.assign(existente, ubicacion);
      } else {
        almacen.ubicaciones.unshift(ubicacion);
      }

      this.almacenesCatalogo = catalogo;
    },

    aplicarUbicacionAtajoAItem(item) {
      if (!item.id_almacen || !item.id_ubicacion_almacen) {
        this.$toast?.info('Selecciona una ubicación completa antes de aplicarla a las unidades.');
        return;
      }

      item.unidades = (item.unidades || []).map((unidad) => ({
        ...unidad,
        id_almacen: item.id_almacen,
        id_ubicacion_almacen: item.id_ubicacion_almacen,
      }));
      item.atajoUbicacionAplicado = true;
      this.$toast?.success('La ubicación se aplicó a todas las unidades de este producto.');
    },

    aplicarCategoriaAtajoAItem(item) {
      const categoria = item.atajoCategoriaUnidad || 'nuevo';
      item.unidades = (item.unidades || []).map((unidad) => ({
        ...unidad,
        categoria_unidad: categoria,
      }));
      item.atajoCategoriaAplicada = true;
      this.$toast?.success('La categoría se aplicó a todas las unidades de este producto.');
    },

    aplicarModeloAtajoAItem(item) {
      if (!item.atajoModeloProducto) {
        this.$toast?.info('Selecciona un modelo antes de aplicarlo a las unidades.');
        return;
      }

      item.unidades = (item.unidades || []).map((unidad) => ({
        ...unidad,
        id_modelo_producto: String(item.atajoModeloProducto || ''),
      }));
      item.atajoModeloAplicado = true;
      this.$toast?.success('El modelo se aplicó a todas las unidades de este producto.');
    },

    crearModeloVacio() {
      return {
        id_modelo_producto: null,
        nombre: '',
        modelo: '',
        uid: 'modelo-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      };
    },

    agregarModeloAProducto(producto) {
      if (!producto.modelos) {
        this.$set ? this.$set(producto, 'modelos', []) : (producto.modelos = []);
      }
      producto.modelos.push(this.crearModeloVacio());
    },

    eliminarModeloDeProducto(producto, index) {
      if (!producto?.modelos) return;
      producto.modelos.splice(index, 1);
    },

    normalizarModelosProducto(modelos) {
      return (Array.isArray(modelos) ? modelos : [])
        .map((modelo) => ({
          id_modelo_producto: modelo.id_modelo_producto || null,
          nombre: String(modelo.nombre || '').trim(),
          modelo: String(modelo.modelo || '').trim(),
          uid: modelo.uid || null,
        }))
        .filter((modelo) => modelo.nombre || modelo.modelo);
    },

    modelosDisponiblesParaItem(item) {
      if (!item) return [];
      if (item.tipoProducto === 'existente') {
        const producto = this.productosPrincipales.find(
          (prod) => String(prod.id_producto) === String(item.id_producto)
        );
        return Array.isArray(producto?.modelos) ? producto.modelos : [];
      }

      return this.normalizarModelosProducto(item.productoNuevo?.modelos || [])
        .filter((modelo) => modelo.nombre && modelo.modelo);
    },

    requiereModeloEnItem(item) {
      return this.modelosDisponiblesParaItem(item).length > 0;
    },

    crearItemVacio() {
      return {
        uid: 'item-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        tipoProducto: 'existente', // 'existente' | 'nuevo'
        id_producto: '',
        productoNuevo: {
          nombre: '',
          descripcion: '',
          id_categoria: '',
          tipo_producto: 'principal',
          imagen_principal_base64: '',
          imagen_principal_nombre: '',
          imagen_principal_preview: '',
          modelos: [],
        },
        complementos: [], // { tipo, id_producto, productoNuevo }
        _nuevoComplementoExistenteId: '',
        cantidad: 1,
        unidades: [],
        id_almacen: '',
        id_ubicacion_almacen: '',
        atajoUbicacionAplicado: false,
        atajoCategoriaUnidad: 'nuevo',
        atajoCategoriaAplicada: false,
        atajoModeloProducto: '',
        atajoModeloAplicado: false,
        comentarios: '',
      };
    },

    async cargarCatalogos() {
      this.cargandoInicial = true;
      try {
        const [resPrincipales, resComplementos, resCategorias, resAlmacenes] = await Promise.all([
          axios.get('/api/almacen/productos/por-tipo', {
            params: { tipo_producto: 'principal' },
          }),
          axios.get('/api/almacen/productos/por-tipo', {
            params: { tipo_producto: 'complemento' },
          }),
          axios.get('/api/almacen/categorias/listar'),
          axios.get('/api/almacen/almacenes/catalogo'),
        ]);

        this.productosPrincipales = Array.isArray(resPrincipales.data)
          ? resPrincipales.data
          : [];

        this.productosComplemento = Array.isArray(resComplementos.data)
          ? resComplementos.data
          : [];

        this.categorias = Array.isArray(resCategorias.data) ? resCategorias.data : [];
        this.almacenesCatalogo = resAlmacenes.data?.data || [];
      } catch (error) {
        console.error('Error al cargar catálogos de almacén', error);
        this.$toast?.error('No se pudieron cargar los catálogos de almacén.');
      } finally {
        this.cargandoInicial = false;
      }
    },

    agregarItem() {
      const item = this.crearItemVacio();
      this.items.push(item);
    },

    eliminarItem(index) {
      this.items.splice(index, 1);
    },

    cambiarTipoProducto(item, tipo) {
      if (item.tipoProducto === tipo) return;

      // Reset dependencias del tipo de producto
      item.id_producto = '';
      item.productoNuevo = {
        nombre: '',
        descripcion: '',
        id_categoria: '',
        tipo_producto: 'principal',
        imagen_principal_base64: '',
        imagen_principal_nombre: '',
        imagen_principal_preview: '',
        modelos: [],
      };
      item.complementos = [];
      item._nuevoComplementoExistenteId = '';
      item.unidades = [];
      item.id_almacen = '';
      item.id_ubicacion_almacen = '';
      item.atajoUbicacionAplicado = false;
      item.atajoCategoriaUnidad = 'nuevo';
      item.atajoCategoriaAplicada = false;
      item.atajoModeloProducto = '';
      item.atajoModeloAplicado = false;

      // Normalizamos cantidad
      if (!item.cantidad || item.cantidad < 1) {
        item.cantidad = 1;
      }

      item.tipoProducto = tipo;

      if (tipo === 'nuevo') {
        // Para producto nuevo, generamos unidades de inmediato (sin complementos)
        this.generarUnidades(item);
      }
      // Para producto existente, esperamos a que seleccione un producto
    },

    alSeleccionarProducto(item, prod) {
      item.id_producto = prod?.id_producto || '';
      item.unidades = [];
      item.complementos = [];
      item._nuevoComplementoExistenteId = '';
      item.id_almacen = '';
      item.id_ubicacion_almacen = '';
      item.atajoUbicacionAplicado = false;
      item.atajoCategoriaUnidad = 'nuevo';
      item.atajoCategoriaAplicada = false;
      item.atajoModeloProducto = '';
      item.atajoModeloAplicado = false;

      if (item.id_producto) {
        this.cargarComplementosExistentes(item);
      }
    },

    async cargarComplementosExistentes(item) {
      try {
        const res = await axios.get(
          '/api/almacen/productos/complementos/' + item.id_producto
        );

        const lista = Array.isArray(res.data) ? res.data : [];

        item.complementos = lista.map((c) => ({
          tipo: 'existente',
          id_producto: c.id_producto,
          productoNuevo: null,
        }));

        this.sincronizarComplementosEnUnidades(item);
        this.generarUnidades(item);
      } catch (error) {
        console.error('Error al obtener complementos del producto', error);
        this.$toast?.error(
          'No se pudieron cargar los complementos del producto seleccionado.'
        );
      }
    },

    agregarComplementoExistente(item) {
      const idSel = item._nuevoComplementoExistenteId;
      if (!idSel) return;

      const idNum = Number(idSel);
      const yaExiste = item.complementos.some(
        (c) => c.tipo === 'existente' && Number(c.id_producto) === idNum
      );
      if (yaExiste) {
        this.$toast?.info('Ese complemento ya está agregado a este producto.');
        return;
      }

      const prod = this.productosComplemento.find(
        (p) => Number(p.id_producto) === idNum
      );
      if (!prod) {
        this.$toast?.error('No se encontró el complemento seleccionado.');
        return;
      }

      item.complementos.push({
        tipo: 'existente',
        id_producto: prod.id_producto,
        productoNuevo: null,
      });

      item._nuevoComplementoExistenteId = '';
      this.sincronizarComplementosEnUnidades(item);
    },

    agregarComplementoNuevo(item) {
      item.complementos.push({
        tipo: 'nuevo',
        id_producto: null,
        productoNuevo: {
          nombre: '',
          descripcion: '',
          id_categoria: '',
          tipo_producto: 'complemento',
          imagen_principal_base64: '',
          imagen_principal_nombre: '',
          imagen_principal_preview: '',
          modelos: [],
        },
      });

      this.sincronizarComplementosEnUnidades(item);
    },

    eliminarComplemento(item, index) {
      item.complementos.splice(index, 1);
      this.sincronizarComplementosEnUnidades(item);
    },

    etiquetaComplemento(comp) {
      if (comp.tipo === 'existente') {
        const prod = this.productosComplemento.find(
          (p) => Number(p.id_producto) === Number(comp.id_producto)
        );
        return prod ? prod.nombre : `Complemento #${comp.id_producto}`;
      }

      return comp.productoNuevo?.nombre || 'Complemento nuevo';
    },

    nombreComplementoPorIndice(item, indice) {
      const comp = item.complementos[indice];
      if (!comp) return 'Complemento';

      return this.etiquetaComplemento(comp);
    },

    nombreProductoParaItem(item) {
      if (item.tipoProducto === 'existente') {
        const p = this.productosPrincipales.find(
          (prod) => Number(prod.id_producto) === Number(item.id_producto)
        );
        return p ? p.nombre : 'Producto';
      }
      return item.productoNuevo.nombre || 'Producto';
    },

    sincronizarComplementosEnUnidades(item) {
      const indicesComplementos = item.complementos.map((_, idx) => idx);

      item.unidades = (item.unidades || []).map((unidad) => {
        const prevCompSeries = {};
        (unidad.complementos || []).forEach((c) => {
          if (typeof c.indice_complemento === 'number') {
            prevCompSeries[c.indice_complemento] = c.serie || '';
          }
        });

        return {
          serie_principal: unidad.serie_principal || '',
          categoria_unidad: unidad.categoria_unidad || 'nuevo',
          id_modelo_producto: unidad.id_modelo_producto || '',
          descripcion_estado: unidad.descripcion_estado || '',
          imagen_estado_base64: unidad.imagen_estado_base64 || '',
          imagen_estado_nombre: unidad.imagen_estado_nombre || '',
          imagen_estado_preview: unidad.imagen_estado_preview || '',
          id_almacen: unidad.id_almacen || '',
          id_ubicacion_almacen: unidad.id_ubicacion_almacen || '',
          complementos: indicesComplementos.map((i) => ({
            indice_complemento: i,
            serie: prevCompSeries[i] || '',
          })),
        };
      });
    },

    generarUnidades(item) {
      const cantidad = Number(item.cantidad) || 0;
      if (cantidad <= 0) {
        item.unidades = [];
        return;
      }

      const prevUnidades = item.unidades || [];
      const indicesComplementos = item.complementos.map((_, idx) => idx);

      item.unidades = Array.from({ length: cantidad }, (_, index) => {
        const anterior = prevUnidades[index] || {};
        const usarAtajoUbicacion = !anterior.serie_principal && !anterior.id_ubicacion_almacen && item.atajoUbicacionAplicado;
        const categoriaPorDefecto = item.atajoCategoriaAplicada
          ? (item.atajoCategoriaUnidad || 'nuevo')
          : 'nuevo';
        const modeloPorDefecto = item.atajoModeloAplicado
          ? String(item.atajoModeloProducto || '')
          : '';
        const prevCompSeries = {};
        (anterior.complementos || []).forEach((c) => {
          if (typeof c.indice_complemento === 'number') {
            prevCompSeries[c.indice_complemento] = c.serie || '';
          }
        });

        return {
          serie_principal: anterior.serie_principal || '',
          categoria_unidad: anterior.categoria_unidad || categoriaPorDefecto,
          id_modelo_producto: anterior.id_modelo_producto || modeloPorDefecto,
          descripcion_estado: anterior.descripcion_estado || '',
          imagen_estado_base64: anterior.imagen_estado_base64 || '',
          imagen_estado_nombre: anterior.imagen_estado_nombre || '',
          imagen_estado_preview: anterior.imagen_estado_preview || '',
          id_almacen: anterior.id_almacen || (usarAtajoUbicacion ? item.id_almacen || '' : ''),
          id_ubicacion_almacen: anterior.id_ubicacion_almacen || (usarAtajoUbicacion ? item.id_ubicacion_almacen || '' : ''),
          complementos: indicesComplementos.map((i) => ({
            indice_complemento: i,
            serie: prevCompSeries[i] || '',
          })),
        };
      });
    },

    ajustarCantidad(item, delta) {
      let nueva = Number(item.cantidad) || 1;
      nueva += delta;
      if (nueva < 1) nueva = 1;
      item.cantidad = nueva;
      this.generarUnidades(item);
    },

    onCantidadChange(item) {
      if (!item.cantidad || item.cantidad < 1) {
        item.cantidad = 1;
      } else {
        item.cantidad = Math.floor(item.cantidad);
      }
      this.generarUnidades(item);
    },

    async guardarOrden() {
      if (!this.items.length) return;

      this.guardando = true;

      try {
        for (const item of this.items) {
          const modelosProducto = this.normalizarModelosProducto(item.productoNuevo?.modelos || []);
          const modelosInvalidosProducto = modelosProducto.some((modelo) => !modelo.nombre || !modelo.modelo);
          if (modelosInvalidosProducto) {
            this.$toast?.error('Cada modelo del producto debe tener nombre y modelo.');
            this.guardando = false;
            return;
          }

          for (const comp of item.complementos || []) {
            const modelosComplemento = this.normalizarModelosProducto(comp.productoNuevo?.modelos || []);
            const modelosInvalidosComplemento = modelosComplemento.some((modelo) => !modelo.nombre || !modelo.modelo);
            if (modelosInvalidosComplemento) {
              this.$toast?.error('Cada modelo de complemento debe tener nombre y modelo.');
              this.guardando = false;
              return;
            }
          }

          if (this.requiereModeloEnItem(item)) {
            const unidadSinModelo = (item.unidades || []).find((unidad) => !unidad.id_modelo_producto);
            if (unidadSinModelo) {
              this.$toast?.error('Selecciona el modelo del producto en cada unidad antes de guardar la orden.');
              this.guardando = false;
              return;
            }
          }
        }

        const payload = {
          encabezado: this.encabezado,
          items: this.items.map((item) => {
            const tipo = item.tipoProducto === 'nuevo' ? 'nuevo' : 'existente';

            const complementos = item.complementos.map((comp) => {
              if (comp.tipo === 'existente') {
                return {
                  tipo: 'existente',
                  id_producto: comp.id_producto,
                };
              }

              return {
                tipo: 'nuevo',
                producto: {
                  nombre: comp.productoNuevo.nombre,
                  descripcion: comp.productoNuevo.descripcion,
                  id_categoria: comp.productoNuevo.id_categoria || null,
                  tipo_producto: 'complemento',
                  imagen_principal_base64:
                    comp.productoNuevo.imagen_principal_base64 || null,
                  imagen_principal_nombre:
                    comp.productoNuevo.imagen_principal_nombre || null,
                  modelos: this.normalizarModelosProducto(comp.productoNuevo.modelos || []),
                },
              };
            });

            const unidades = (item.unidades || []).map((u) => ({
              serie_principal: u.serie_principal,
              categoria_unidad: u.categoria_unidad || 'nuevo',
              id_modelo_producto: item.tipoProducto === 'existente' ? (u.id_modelo_producto || '') : '',
              modelo_ref: u.id_modelo_producto || '',
              descripcion_estado: u.descripcion_estado || '',
              imagen_estado_base64: u.imagen_estado_base64 || null,
              imagen_estado_nombre: u.imagen_estado_nombre || null,
              id_almacen: u.id_almacen || '',
              id_ubicacion_almacen: u.id_ubicacion_almacen || '',
              complementos: (u.complementos || []).map((uc) => ({
                indice_complemento: uc.indice_complemento,
                serie: uc.serie,
              })),
            }));

            return {
              tipo,
              id_producto:
                tipo === 'existente' ? item.id_producto || null : null,
              producto:
                tipo === 'nuevo'
                  ? {
                      nombre: item.productoNuevo.nombre,
                      descripcion: item.productoNuevo.descripcion,
                      id_categoria: item.productoNuevo.id_categoria || null,
                      tipo_producto: 'principal',
                      imagen_principal_base64:
                        item.productoNuevo.imagen_principal_base64 || null,
                      imagen_principal_nombre:
                        item.productoNuevo.imagen_principal_nombre || null,
                      modelos: this.normalizarModelosProducto(item.productoNuevo.modelos || []),
                    }
                  : null,
              cantidad: item.cantidad,
              comentarios: item.comentarios,
              id_almacen: '',
              id_ubicacion_almacen: '',
              complementos,
              unidades,
            };
          }),
        };

        const res = await axios.post(
          '/api/almacen/ordenes-entrada/crear-completa',
          payload
        );

        if (res.data?.status === 'ok') {
          this.$toast?.success('Orden de entrada registrada correctamente.');
          // Reiniciamos formulario
          this.encabezado = {
            fecha: new Date().toISOString().slice(0, 10),
            folio: '',
            proveedor: '',
            solicito: '',
            entrego: '',
            recibio: '',
            direccion_recepcion: '',
            comentarios: '',
          };
          this.items = [];
          this.agregarItem();
        } else {
          this.$toast?.error(
            res.data?.message || 'No se pudo registrar la orden de entrada.'
          );
        }
      } catch (error) {
        console.error('Error al guardar orden de entrada:', error);
        const msg =
          error.response?.data?.message ||
          'Ocurrió un error al guardar la orden de entrada.';
        this.$toast?.error(msg);
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>


<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Header */
.entrada-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.entrada-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Meta */
.entrada-meta {
  border-radius: 1.25rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.92);
  padding: 0.7rem 0.7rem 0.6rem;
  margin-bottom: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.entrada-meta__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.entrada-field {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  min-width: 0;
}

.entrada-field--grow {
  flex: 1 1 0;
}

.entrada-field--inline {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.entrada-label {
  font-size: 0.78rem;
  color: #cbd5f5;
}

.entrada-input {
  border-radius: 0.8rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.entrada-input--textarea {
  resize: vertical;
}

.entrada-input--compact {
  padding-inline: 0.5rem;
  min-width: 180px;
}

.entrada-input--counter {
  max-width: 80px;
  text-align: center;
}

.entrada-input::placeholder {
  color: #6b7280;
}

.entrada-input:focus {
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.7);
}

/* Items */
.entrada-items {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.entrada-item-card {
  border-radius: 1.3rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.7rem 0.7rem 0.6rem;
}

.entrada-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.entrada-item-badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.96);
}

.entrada-item-remove {
  border: none;
  background: transparent;
  color: #fca5a5;
  font-size: 0.78rem;
  cursor: pointer;
}

.entrada-item-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

.entrada-item-row--two-cols .entrada-field {
  flex: 1 1 0;
}

.entrada-item-row--inner {
  margin-top: 0.2rem;
}

.entrada-shortcut-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.55rem;
}

.entrada-bulk-shortcuts {
  margin-bottom: 0.7rem;
}

.entrada-bulk-shortcuts__grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 0.65rem;
}

.entrada-bulk-shortcuts__card {
  grid-column: span 4;
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.42);
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.94),
    rgba(30, 41, 59, 0.88)
  );
  padding: 0.75rem;
}

.entrada-bulk-shortcuts__card--wide {
  grid-column: span 12;
}


.entrada-hint--mb {
  margin-bottom: 0.6rem;
}

/* Toggle tipo producto */
.entrada-toggle {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.15rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.7);
}

.entrada-toggle-pill {
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #cbd5f5;
  font-size: 0.78rem;
  padding: 0.25rem 0.7rem;
  cursor: pointer;
  transition:
    background 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease;
}

.entrada-toggle-pill--active {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.6),
    rgba(37, 99, 235, 1)
  );
  color: #e5e7eb;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.95);
}

/* Secciones de item */
.entrada-item-section {
  margin-top: 0.4rem;
  padding-top: 0.5rem;
  border-top: 1px dashed rgba(148, 163, 184, 0.5);
}

.entrada-item-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.3rem;
  gap: 0.5rem;
}

.entrada-item-section-actions {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.entrada-photo-box {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.entrada-photo-box--compact {
  min-width: 150px;
}

.entrada-photo-preview,
.entrada-photo-empty {
  width: 100%;
  min-width: 150px;
  height: 120px;
  border-radius: 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.86);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.78rem;
}

.entrada-photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.entrada-photo-input {
  display: none;
}

.entrada-photo-actions {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

/* Chips complementos */
.entrada-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.entrada-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
  font-size: 0.75rem;
  color: #e5e7eb;
}

.entrada-chip-main {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.entrada-chip-tag {
  font-size: 0.68rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: rgba(129, 140, 248, 0.12);
  border: 1px solid rgba(129, 140, 248, 0.7);
}

.entrada-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #e5e7eb;
  font-size: 0.8rem;
}

.entrada-chip-button {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.98);
  color: #e5e7eb;
  font-size: 0.75rem;
  padding: 0.25rem 0.7rem;
  cursor: pointer;
  white-space: nowrap;
}

/* Hints */
.entrada-hint {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Counter */
.entrada-counter {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.entrada-counter-btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* Unidades: ahora crece vertical, SIN scroll interno */
.entrada-units-list {
  margin-top: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.entrada-unit-card {
  border-radius: 1rem;
  padding: 0.55rem 0.55rem 0.5rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.32),
    rgba(15, 23, 42, 0.95)
  );
  border: 1px solid rgba(148, 163, 184, 0.55);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.75);
}

.entrada-unit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.entrada-unit-badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.08rem 0.4rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.96);
}

.entrada-unit-product {
  font-size: 0.78rem;
  color: #e5e7eb;
}

.entrada-label-pill {
  font-size: 0.7rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

/* Botón agregar item */
.entrada-add-item {
  margin-top: 0.4rem;
}

/* Botones iOS */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .entrada-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .entrada-meta__row {
    flex-direction: column;
  }

  .entrada-item-row {
    flex-direction: column;
  }

  .entrada-item-section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .entrada-item-section-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .entrada-input--compact {
    width: 100%;
  }

  .entrada-bulk-shortcuts__grid {
    grid-template-columns: 1fr;
  }

  .entrada-bulk-shortcuts__card,
  .entrada-bulk-shortcuts__card--wide {
    grid-column: span 1;
  }
}
</style>
